import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiClient, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    // See lib/api.js - fires once the refresh-token itself has failed, i.e. the session is
    // definitively over. This is the only place mid-session expiry gets detected; without
    // it, `user` would stay truthy until the next full page load even though every API call
    // is 401ing.
    const handleSessionExpired = () => setUser(false);
    window.addEventListener("yoko:session-expired", handleSessionExpired);
    return () => window.removeEventListener("yoko:session-expired", handleSessionExpired);
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await apiClient.post("/auth/login", { email, password });
      if (data.mfa_setup_required) {
        return { mfaSetupRequired: true, setupToken: data.setup_token, email: data.email };
      }
      if (data.mfa_challenge_required) {
        return { mfaChallengeRequired: true, challengeToken: data.challenge_token, email: data.email };
      }
      setUser(data);
      return { success: true, user: data };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const applyAuthenticatedUser = (userData) => setUser(userData);

  const logout = async () => {
    try {
      await apiClient.post("/auth/logout");
    } finally {
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, logout, refreshUser, applyAuthenticatedUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
